import { useState, useCallback, useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  ScrollView,
  Platform,
  Alert,
} from 'react-native';
import { useRouter, Stack } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { MapPin, Search, X, CheckCircle, ChevronLeft, Plus } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';

const log = createLogger('CHANGE_GYM');

const GOOGLE_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? '';
const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';
const SUPABASE_ANON = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '';

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: { main_text: string; secondary_text: string };
}

async function searchPlaces(query: string, loc?: { lat: number; lng: number } | null): Promise<PlacePrediction[]> {
  if (query.trim().length < 2) return [];
  if (SUPABASE_URL) {
    try {
      const body: Record<string, unknown> = { input: query.trim(), apiKey: GOOGLE_API_KEY || undefined };
      if (loc) { body.location = `${loc.lat},${loc.lng}`; body.radius = 50000; }
      const res = await fetch(`${SUPABASE_URL}/functions/v1/places-autocomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${SUPABASE_ANON}`, apikey: SUPABASE_ANON },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (json.predictions?.length > 0) return json.predictions;
    } catch (err) { log.warn('Edge function error', err); }
  }
  return [];
}

async function getPlaceDetails(placeId: string) {
  if (SUPABASE_URL) {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/places-detail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${SUPABASE_ANON}`, apikey: SUPABASE_ANON },
        body: JSON.stringify({ place_id: placeId, apiKey: GOOGLE_API_KEY || undefined }),
      });
      return await res.json();
    } catch (err) { log.warn('Place details edge error', err); }
  }
  return null;
}

export default function ChangeGymScreen() {
  const router = useRouter();
  const { user, refreshProfile } = useAuth();
  const { theme } = useTheme();

  const [gymQuery, setGymQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [gymResults, setGymResults] = useState<{ id: string; name: string; address?: string; isPlace?: boolean }[]>([]);
  const [selectedGym, setSelectedGym] = useState<{ id: string; name: string; address?: string } | null>(null);
  const [gymLoading, setGymLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const locationRef = useRef<{ lat: number; lng: number } | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cityFilterRef = useRef('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data.latitude && data.longitude) locationRef.current = { lat: data.latitude, lng: data.longitude };
      } catch {}
    })();
  }, []);

  const searchGyms = useCallback((q: string) => {
    setGymQuery(q);
    if (q.trim().length < 2) { setGymResults([]); return; }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setGymLoading(true);
      try {
        const predictions = await searchPlaces(q, locationRef.current);
        const results: { id: string; name: string; address?: string; isPlace?: boolean }[] = [];
        if (predictions.length > 0) {
          results.push(...predictions.map((p) => ({
            id: p.place_id, name: p.structured_formatting.main_text,
            address: p.structured_formatting.secondary_text, isPlace: true,
          })));
        }
        try {
          let query = supabase.from('gyms').select('id, name, city, address')
            .ilike('name', `%${q.trim()}%`)
            .limit(10);
          const city = cityFilterRef.current.trim();
          if (city.length > 1) {
            query = query.ilike('city', `%${city}%`);
          }
          const { data } = await query;
          if (data?.length) {
            const existingIds = new Set(results.map(r => r.name.toLowerCase()));
            for (const g of data) {
              if (!existingIds.has(g.name.toLowerCase())) {
                const displayName = g.city ? `${g.name} · ${g.city}` : g.name;
                results.push({ id: g.id, name: displayName, address: g.address ?? g.city ?? undefined });
              }
            }
          }
        } catch {}
        setGymResults(results);
      } catch { setGymResults([]); }
      setGymLoading(false);
    }, 350);
  }, []);

  const selectGym = useCallback(async (item: { id: string; name: string; address?: string; isPlace?: boolean }) => {
    setGymResults([]);
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(item.id);
    if (isUuid) { setSelectedGym({ id: item.id, name: item.name, address: item.address }); return; }

    setGymLoading(true);
    try {
      const details = await getPlaceDetails(item.id);
      const placeId = details?.place_id || item.id;
      const placeName = details?.name || item.name;
      const placeAddr = details?.formatted_address || item.address || '';
      const placeLat = details?.geometry?.location?.lat ?? 0;
      const placeLng = details?.geometry?.location?.lng ?? 0;

      // Check if gym already exists in gyms table by google_place_id
      const { data: existingGym } = await supabase.from('gyms')
        .select('id, name').eq('google_place_id', placeId).limit(1).maybeSingle();

      if (existingGym) {
        setSelectedGym({ id: existingGym.id, name: existingGym.name, address: placeAddr, placeId, lat: placeLat, lng: placeLng } as any);
      } else {
        // Store place details so saveGym can create the gyms record
        setSelectedGym({ id: `place:${placeId}`, name: placeName, address: placeAddr, placeId, lat: placeLat, lng: placeLng } as any);
      }

      // Also ensure organization exists
      const { data: existingOrg } = await supabase.from('organizations').select('id, name')
        .eq('google_place_id', placeId).limit(1).maybeSingle();
      if (!existingOrg) {
        try {
          await supabase.from('organizations').insert({
            name: placeName, org_type: 'gym', google_place_id: placeId, address: placeAddr,
            latitude: placeLat, longitude: placeLng, created_by: user?.id,
          });
        } catch {}
      }
    } catch (err) {
      log.warn('Place details error', err);
      await createCustomGym(item.name);
    }
    setGymLoading(false);
  }, []);

  const createCustomGym = async (name: string) => {
    try {
      const { data: newOrg, error } = await supabase.from('organizations')
        .insert({ name: name.trim(), org_type: 'gym', created_by: user?.id }).select('id, name').single();
      if (error) { setSelectedGym({ id: 'pending', name: name.trim() }); }
      else if (newOrg) setSelectedGym({ id: newOrg.id, name: newOrg.name });
    } catch { setSelectedGym({ id: 'pending', name: name.trim() }); }
  };

  const saveGym = async (setPrimary: boolean) => {
    if (!user?.id || !selectedGym) return;
    setSaving(true);
    try {
      // Find or create a `gyms` table record (profiles.gym_id FK → gyms)
      let gymTableId: string | null = null;
      const gymData = selectedGym as any;
      const isHomeGym = gymData.isHomeGym === true || selectedGym.id === 'home_gym';
      const isExistingGym = !isHomeGym && !selectedGym.id.startsWith('place:') && selectedGym.id !== 'pending' && /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(selectedGym.id);

      if (isExistingGym) {
        gymTableId = selectedGym.id;
      } else if (isHomeGym) {
        // Create a home gym entry
        const { data: newGym, error: gymError } = await supabase.from('gyms')
          .insert({
            name: selectedGym.name,
            address: 'Home Gym',
            lat: 0, lng: 0,
            is_home_gym: true,
            created_by: user!.id,
          })
          .select('id').single();
        if (gymError) log.warn('Failed to create home gym:', gymError);
        if (newGym) gymTableId = newGym.id;
      } else if (selectedGym.id !== 'pending') {
        // Check if gym exists by name
        const { data: existingGym } = await supabase.from('gyms')
          .select('id').eq('name', selectedGym.name).limit(1).maybeSingle();
        if (existingGym) {
          gymTableId = existingGym.id;
        } else {
          // Create gym with lat/lng from place details
          const { data: newGym, error: gymError } = await supabase.from('gyms')
            .insert({
              name: selectedGym.name,
              address: selectedGym.address ?? null,
              lat: gymData.lat ?? 0,
              lng: gymData.lng ?? 0,
              google_place_id: gymData.placeId ?? null,
              created_by: user!.id,
            })
            .select('id').single();
          if (gymError) log.warn('Failed to create gym:', gymError);
          if (newGym) gymTableId = newGym.id;
        }
      }

      // Link to gym_memberships (always, regardless of primary)
      if (gymTableId) {
        try {
          await supabase.from('gym_memberships').upsert(
            { gym_id: gymTableId, user_id: user.id, role: 'member', status: 'active' },
            { onConflict: 'gym_id,user_id' },
          );
        } catch {}
      }

      if (setPrimary) {
        // Update profile's primary gym fields
        const updates: Record<string, unknown> = {
          home_gym_name: selectedGym.name,
          home_gym_address: selectedGym.address ?? null,
        };
        if (gymTableId) {
          if (isHomeGym) {
            updates.home_gym_id = gymTableId;
            updates.gym_id = null;
          } else {
            updates.gym_id = gymTableId;
            updates.home_gym_id = null;
          }
        }
        const { error: profileError } = await supabase.from('profiles').update(updates).eq('id', user.id);
        if (profileError) log.warn('Profile update error:', profileError);

        // Link to organization_memberships as primary
        if (selectedGym.id !== 'pending') {
          try {
            await supabase.from('organization_memberships').upsert(
              { org_id: selectedGym.id, user_id: user.id, role: 'member', is_primary_for_type: true, status: 'active' },
              { onConflict: 'org_id,user_id' },
            );
          } catch {}
        }
      } else {
        // Add only: link organization_memberships without primary flag
        if (selectedGym.id !== 'pending') {
          try {
            await supabase.from('organization_memberships').upsert(
              { org_id: selectedGym.id, user_id: user.id, role: 'member', is_primary_for_type: false, status: 'active' },
              { onConflict: 'org_id,user_id' },
            );
          } catch {}
        }
      }

      await refreshProfile?.(true);
    } catch (err) {
      log.warn('Failed to save gym', err);
    }
    setSaving(false);
    if (router.canGoBack()) router.back();
    else router.replace('/(tabs)/profile');
  };

  const handleSavePress = () => {
    if (!selectedGym) return;
    const gymData = selectedGym as any;
    const isHomeGym = gymData.isHomeGym === true || selectedGym.id === 'home_gym';
    if (isHomeGym) {
      // Home gyms are always primary
      saveGym(true);
      return;
    }
    Alert.alert(
      'Make Primary Gym?',
      `Do you want to set ${selectedGym.name} as your primary gym? This will update your leaderboard placement.`,
      [
        { text: 'Add Only', onPress: () => saveGym(false) },
        { text: 'Set as Primary', style: 'default', onPress: () => saveGym(true) },
      ],
    );
  };

  const showCreateOption = gymQuery.trim().length >= 2 && !gymLoading && gymResults.length === 0;

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <Stack.Screen options={{ headerShown: false }} />
      <TouchableOpacity style={styles.backBtn} onPress={() => router.canGoBack() ? router.back() : router.replace('/(tabs)/profile')}>
        <ChevronLeft size={24} color={theme.colors.text} />
      </TouchableOpacity>

      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.inner}>
          <View style={[styles.iconCircle, { backgroundColor: theme.colors.accent + '20' }]}>
            <MapPin size={48} color={theme.colors.accent} />
          </View>

          <Text style={[styles.title, { color: theme.colors.text, fontFamily: TYPO.heading.bold }]}>
            Change Home Gym
          </Text>
          <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
            Search for your gym, create a new one, or set up a home gym.
          </Text>

          {/* Home Gym Option */}
          {!selectedGym && (
            <TouchableOpacity
              style={{ backgroundColor: theme.colors.surface, borderRadius: 2, padding: 14, width: '100%', borderWidth: 1, borderColor: theme.colors.border, flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 4 }}
              onPress={() => {
                const name = Platform.OS === 'web'
                  ? window.prompt('Name your home gym:', 'My Home Gym')
                  : 'My Home Gym';
                if (!name) return;
                setSelectedGym({ id: 'home_gym', name, address: 'Home Gym', isHomeGym: true } as any);
              }}>
              <Text style={{ fontSize: 24 }}>🏠</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 15, fontWeight: '700', color: theme.colors.text }}>I Lift at Home</Text>
                <Text style={{ fontSize: 12, color: theme.colors.textSecondary }}>Create a personal home gym (not in Gym Battle)</Text>
              </View>
            </TouchableOpacity>
          )}

          {!selectedGym && (
            <TextInput
              style={[styles.input, { color: theme.colors.text, borderColor: theme.colors.border, backgroundColor: theme.colors.surface, marginBottom: 8 }]}
              placeholder="Filter by city (optional)..."
              placeholderTextColor={theme.colors.textTertiary}
              value={cityFilter}
              onChangeText={(v) => { setCityFilter(v); cityFilterRef.current = v; }}
              autoCapitalize="words"
            />
          )}

          {selectedGym ? (
            <View style={[styles.gymSelected, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
              <View style={styles.gymSelectedRow}>
                <CheckCircle size={20} color={theme.colors.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.gymSelectedText, { color: theme.colors.text, fontFamily: TYPO.body.semibold }]}>
                    {selectedGym.name}
                  </Text>
                  {selectedGym.address && (
                    <Text style={{ color: theme.colors.textTertiary, fontSize: 12, marginTop: 2 }}>{selectedGym.address}</Text>
                  )}
                </View>
              </View>
              <TouchableOpacity onPress={() => { setSelectedGym(null); setGymQuery(''); setGymResults([]); }}>
                <X size={18} color={theme.colors.textTertiary} />
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <View style={[styles.searchBox, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
                <Search size={20} color={theme.colors.textTertiary} />
                <TextInput
                  style={[styles.searchInput, { color: theme.colors.text }]}
                  placeholder="Search gyms…"
                  placeholderTextColor={theme.colors.textTertiary}
                  value={gymQuery}
                  onChangeText={searchGyms}
                  autoCapitalize="words"
                  autoCorrect={false}
                />
                {gymLoading && <ActivityIndicator size="small" color={theme.colors.accent} />}
              </View>

              {gymResults.length > 0 && (
                <View style={[styles.gymList, { borderColor: theme.colors.border }]}>
                  {gymResults.map((g, idx) => (
                    <TouchableOpacity
                      key={`${g.id}-${idx}`}
                      style={[styles.gymItem, { borderBottomColor: theme.colors.border }]}
                      onPress={() => selectGym(g)}>
                      <Text style={[styles.gymItemName, { color: theme.colors.text, fontFamily: TYPO.body.semibold }]}>{g.name}</Text>
                      {g.address && <Text style={[styles.gymItemAddr, { color: theme.colors.textTertiary }]}>{g.address}</Text>}
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {showCreateOption && (
                <View style={{ width: '100%', gap: 8, marginTop: 4 }}>
                  <View style={{ padding: 16, alignItems: 'center' }}>
                    <Text style={{ color: theme.colors.textSecondary, fontSize: 14, textAlign: 'center' }}>
                      {"Don't see your gym? Try a different spelling, or contact us at support@douevenlift.com to add it."}
                    </Text>
                  </View>
                  <Text style={{ color: theme.colors.textTertiary, fontSize: 13, textAlign: 'center' }}>Or create a custom entry:</Text>
                  <TouchableOpacity
                    style={[styles.createBtn, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}
                    onPress={() => createCustomGym(gymQuery)}>
                    <Plus size={18} color={theme.colors.accent} />
                    <Text style={[styles.createBtnText, { color: theme.colors.accent, fontFamily: TYPO.body.semibold }]}>
                      Create "{gymQuery.trim()}"
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}
        </View>

        <View style={styles.bottom}>
          <TouchableOpacity
            style={[styles.primaryBtn, {
              backgroundColor: selectedGym ? theme.colors.accent : theme.colors.surface,
              borderWidth: selectedGym ? 0 : 1, borderColor: theme.colors.border,
              opacity: saving ? 0.6 : 1,
            }]}
            onPress={handleSavePress}
            disabled={saving || !selectedGym}>
            <Text style={[styles.primaryBtnText, {
              color: selectedGym ? theme.colors.accentForeground : theme.colors.textTertiary,
              fontFamily: TYPO.heading.bold,
            }]}>
              {saving ? 'Saving…' : 'Save'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/(tabs)/profile')}>
            <Text style={{ color: theme.colors.textSecondary, fontSize: 14, textDecorationLine: 'underline', marginTop: 4 }}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { position: 'absolute', top: 50, left: 16, zIndex: 10, padding: 8 },
  scrollContent: { flexGrow: 1 },
  inner: { flex: 1, paddingHorizontal: 24, paddingTop: 80, alignItems: 'center', gap: 16 },
  iconCircle: { width: 96, height: 96, borderRadius: 4, justifyContent: 'center', alignItems: 'center', marginBottom: 8 },
  title: { fontSize: 24, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 },
  subtitle: { fontSize: 15, textAlign: 'center', lineHeight: 22, maxWidth: 340, marginBottom: 8 },
  searchBox: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 2, paddingHorizontal: 14, height: 52, width: '100%' },
  searchInput: { flex: 1, fontSize: 17, padding: 0 },
  gymList: { borderWidth: 1, borderRadius: 2, overflow: 'hidden', width: '100%', maxHeight: 300 },
  gymItem: { paddingVertical: 12, paddingHorizontal: 14, borderBottomWidth: 1 },
  gymItemName: { fontSize: 15 },
  gymItemAddr: { fontSize: 13, marginTop: 2 },
  gymSelected: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 2, borderRadius: 2, paddingVertical: 14, paddingHorizontal: 16, width: '100%' },
  gymSelectedRow: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 },
  gymSelectedText: { fontSize: 16 },
  createBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderWidth: 1, borderRadius: 2, paddingVertical: 14, paddingHorizontal: 16, width: '100%' },
  createBtnText: { fontSize: 16 },
  bottom: { paddingHorizontal: 24, paddingBottom: 40, gap: 4, alignItems: 'center' },
  primaryBtn: { width: '100%', paddingVertical: 18, borderRadius: 2, alignItems: 'center' },
  primaryBtnText: { fontSize: 18, textTransform: 'uppercase', letterSpacing: 1 },
  input: { borderWidth: 1, borderRadius: 2, paddingHorizontal: 14, height: 44, width: '100%', fontSize: 15 },
});
